import { Button, Pullout, PulloutType } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import _ from 'lodash';
import HtmlParser from 'html-react-parser';
import styled from 'styled-components';
import { AppendDistributionModal } from '../../../components/audience/distributions/AppendDistributionModal';
import { DistributionContactsList } from '../../../components/audience/distributions/DistributionContactsList';
import { DistributionUploadStatusMessage } from '../../../components/audience/distributions/DistributionUploadStatusMessage';
import ContentConstants from '../../../contentConstants';
import {
	AudienceSourceType,
	AudienceStatus,
	DistributionContactsListSourceType,
	DistributionFileUploadType,
	DistributionUploadStatus,
	IAudience,
	IDistributionContactActionCreators,
	IDistributionFile,
	IDistributionFileActionCreators,
	ISurveyAdminSummary,
	ISurveyParticipantActionCreators,
	SurveyStatusType,
} from '../../../models';
import { defaultAudience } from '../../../store/AudienceDetailsStore';
import { IDistributionContactState } from '../../../store/DistributionContactStore';
import { IDistributionFileState } from '../../../store/DistributionFileStore';
import { ISurveyParticipantState } from '../../../store/SurveyParticipantStore';
import { CardLayout, ClearDiv, HeaderButtonContainer } from '../../common';

export type IAdminParticipantsProps = {
	surveyId: number;
	audienceId: number;
	audiences: IAudience[];
	isLoadingAudiences: boolean;
	GetAudiences: (surveyId: number) => void;
	surveyAdminSummary?: ISurveyAdminSummary;
	surveyAdminSummaryLoading?: boolean;
} & IDistributionContactActionCreators &
	IDistributionContactState &
	IDistributionFileActionCreators &
	IDistributionFileState &
	ISurveyParticipantActionCreators &
	ISurveyParticipantState;

export const AddParticipantsButtonContainer = styled.div`
	position: absolute;
	right: 28px;
`;
AddParticipantsButtonContainer.displayName = 'AddParticipantsButton';

export const MessageContainer = styled.div`
	height: 39px;
	width: 623px;
	color: #444444;
	font-family: Montserrat;
	font-size: 15px;
	font-weight: 500;
	line-height: 19px;
`;
MessageContainer.displayName = 'MessageContainer';

export class AdminParticipants extends React.PureComponent<IAdminParticipantsProps, { appendModalOpen: boolean; audience: IAudience }> {
	constructor(props: IAdminParticipantsProps) {
		super(props);

		const audience = _.find(props.audiences, (a) => a.audienceId === props.audienceId) ?? defaultAudience;
		this.state = {
			appendModalOpen: false,
			audience,
		};

		this.appendDistribution = this.appendDistribution.bind(this);
		this.closeModal = this.closeModal.bind(this);
	}

	public componentDidMount() {
		this.props.ClearSelectedDistributionContacts();
	}

	public componentDidUpdate(previousProps: IAdminParticipantsProps) {
		if (this.props.audiences !== previousProps.audiences) {
			this.setState({ audience: _.find(this.props.audiences, (a) => a.audienceId === this.props.audienceId) ?? defaultAudience });
		}
	}

	public render() {
		const { surveyAdminSummary, distribution, distributions, selectedDistribution, surveyId } = this.props;
		const { audience } = this.state;
		const hasBeenCancelled = surveyAdminSummary ? surveyAdminSummary.surveyStatus === SurveyStatusType.Cancelled : false;
		const readOnly = hasBeenCancelled || audience.isClosed;
		const inProgressDistribution = _.find(distributions, (d: IDistributionFile) => {
			return d.distributionUploadStatusId === DistributionUploadStatus.InProgress;
		});

		const headerButton = (
			<HeaderButtonContainer className='tws-add-new-participants-button'>
				<Button disabled={readOnly || inProgressDistribution !== undefined} onClick={() => this.setState({ appendModalOpen: true })}>
					{ContentConstants.SURVEY_ADMIN_PARTICIPANTS_APPENDMODAL_TITLE}
				</Button>
			</HeaderButtonContainer>
		);

		return (
			<CardLayout
				loading={this.props.isLoadingAudiences || this.props.surveyAdminSummaryLoading!}
				title={ContentConstants.SURVEY_ADMIN_ACTION_DISTRIBUTION_LIST}
				header={audience.isClosed || surveyAdminSummary?.audienceSourceType === AudienceSourceType.Hris ? undefined : headerButton}
			>
				{distribution && <DistributionUploadStatusMessage distributions={distributions} selectedDistribution={selectedDistribution} />}
				{hasBeenCancelled && (
					<MessageContainer>
						<b>{ContentConstants.SURVEY_ADMIN_IS_CANCEL_NOTE}</b>
						{ContentConstants.SURVEY_ADMIN_IS_CANCEL_CONTENT}
					</MessageContainer>
				)}
				{audience.isClosed && !hasBeenCancelled && (
					<MessageContainer>
						<b>{ContentConstants.SURVEY_ADMIN_IS_CLOSED_NOTE}</b>
						{ContentConstants.SURVEY_ADMIN_IS_CLOSED_CONTENT}
					</MessageContainer>
				)}
				{(audience.audienceStatus === AudienceStatus.Ready || audience.audienceStatus === AudienceStatus.Publishing) && (
					<>
						<Pullout type={PulloutType.Note}>{HtmlParser(ContentConstants.SURVEYPARTICIPANTS_PUBLISHING_NOTE)}</Pullout>
						<ClearDiv clearHeight='30px' />
					</>
				)}
				<DistributionContactsList
					{...this.props}
					distributionContactsListSourceType={DistributionContactsListSourceType.DistributionList}
					readonly={readOnly}
					surveyId={surveyId}
					audienceId={audience.audienceId}
					distributionType={audience.distributionType}
					surveyStatus={this.props.surveyAdminSummary?.surveyStatus}
					audienceSourceType={this.props.surveyAdminSummary?.audienceSourceType}
				/>
				{this.state.appendModalOpen && (
					<AppendDistributionModal
						titleText={ContentConstants.SURVEY_ADMIN_PARTICIPANTS_APPENDMODAL_TITLE}
						instructionsText={ContentConstants.SURVEY_ADMIN_PARTICIPANTS_APPENDMODAL_INSTRUCTIONS}
						distributionValidationErrors={this.props.distributionValidationErrors}
						distributionValidationWarnings={this.props.distributionValidationWarnings}
						file={this.props.file}
						fileUploadType={this.props.fileUploadType}
						isUploading={this.props.isUploading}
						modalOpen={this.state.appendModalOpen}
						validateOnly={true}
						closeAfterValidate={false}
						closeModal={() => this.closeModal()}
						downloadTemplate={() =>
							this.props.DownloadDistributionTemplate(audience.distributionType!, surveyAdminSummary!.externalApplicationType)
						}
						appendDistribution={this.appendDistribution}
						validateFile={(fut: DistributionFileUploadType, f: any, dt?: number) =>
							this.props.ValidateFile(surveyId, audience.audienceId, fut, f, dt!)
						}
						showUploadButton={true}
						selectedDistribution={this.props.selectedDistribution}
						distributionType={audience.distributionType}
					/>
				)}
			</CardLayout>
		);
	}

	public appendDistribution(file: any, distributionType: number | undefined) {
		const { AppendDistribution, surveyId } = this.props;
		const { audience } = this.state;

		AppendDistribution(surveyId, audience.audienceId, file, distributionType!);
	}

	public closeModal() {
		this.setState({ appendModalOpen: false });
		this.props.ClearDistributionValidation();
		this.refreshParticipants(this.props);
	}

	public refreshParticipants(props: IAdminParticipantsProps) {
		const { pageRange, searchText, sortParams, columns, surveyId } = props;
		const { audience } = this.state;

		this.props.GetDistributions(surveyId, audience.audienceId);
		this.props.GetPagedSurveyParticipants(
			surveyId,
			audience.audienceId,
			pageRange,
			searchText,
			sortParams.sortedBy!,
			sortParams.sortAsc!,
			columns,
		);
	}
}
